import {
  format,
  differenceInMinutes,
  parse,
  subMinutes,
  addMinutes,
  addDays,
  isBefore,
  subDays,
  subMonths,
  lastDayOfMonth,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { defaults } from '@/helpers/appDefaults';
import { convertTo12Hour, kmToMiles } from '@/helpers/convert';
import { LENGTH_UNITS } from '@/modules/Flexigo/helpers/constants';
import { isLocaleEnUs, decimalNumber, isUsDateFormat } from './utility';

/**
 * Normalize time integer to handle midnight wraparound.
 * Converts negative times (e.g., -115 for -1:15) to valid 24-hour format (e.g., 2245 for 22:45).
 * Also handles times >= 2400 by wrapping to next day.
 * @param {number} timeInt - Time in HHMM integer format (can be negative)
 * @returns {number} Normalized time in HHMM format (0-2359)
 */
export function normalizeTimeInt(timeInt) {
  if (timeInt === null || timeInt === undefined) return timeInt;

  // Convert to total minutes for easier calculation
  const hours = Math.floor(Math.abs(timeInt) / 100);
  const minutes = Math.abs(timeInt) % 100;
  let totalMinutes = hours * 60 + minutes;

  // If negative, subtract from 24 hours
  if (timeInt < 0) {
    totalMinutes = 24 * 60 - totalMinutes;
  }

  // Handle overflow (>= 24 hours)
  totalMinutes %= 24 * 60;

  // Convert back to HHMM format
  const normalizedHours = Math.floor(totalMinutes / 60);
  const normalizedMinutes = totalMinutes % 60;

  return normalizedHours * 100 + normalizedMinutes;
}

/**
 * convert time values in to string values
 * 1230 => { hours: '12', minutes: '30' }
 * @param {number|string} val - Time value in HHMM format
 * @returns {{ hours: string, minutes: string }}
 */
export function intToTime(val) {
  const strValue = String(val);
  const hours = strValue.slice(-4, -2) || '0';
  const minutes = strValue.slice(-2);
  return { hours, minutes: minutes.length < 2 ? `0${minutes}` : minutes };
}

/**
 * Converts an HHMM integer/string value to a time string (HH:mm or 12-hour format).
 * Automatically normalizes negative values and values >= 2400.
 *
 * @param {number|string} value - Time value in HHMM format (can be negative)
 * @param {boolean} [is12Hour=false] - Whether to return the time in 12-hour format
 * @returns {string|undefined} Formatted time string or undefined
 */
export function intToTimeStr(value, is12Hour = false) {
  if (value === undefined || value === null) return undefined;
  // Normalize the value to handle negative times and overflow
  const normalizedValue = normalizeTimeInt(Number(value));
  const { hours, minutes } = intToTime(String(normalizedValue));
  const h = hours.length === 1 ? `0${hours}` : hours;
  const m = minutes.length === 1 ? `0${minutes}` : minutes;
  return is12Hour ? convertTo12Hour(`${h}:${m}`) : `${h}:${m}`;
}

/**
 * Creates a UTC Date object from a provided date string.
 *
 * @param {string} dateString - Input date string
 * @returns {Date} UTC Date object
 */
export function createUTCDateFromInput(dateString) {
  return new Date(`${dateString}T00:00:00Z`);
}

/**
 * Creates a Date object in the local timezone at 00:00:00,
 * then returns it as-is (interpreted as local time, not UTC).
 *
 * @param {string} dateString - Date string (ISO-like format, e.g. "2025-12-10")
 * @returns {Date} Date object created in the local timezone
 */
export function createFixedDateUTC(dateString) {
  return new Date(`${dateString}T00:00:00`);
}

/**
 * Returns a date-only format string based on the length unit value
 * (METRIC: dd.MM.yyyy, IMPERIAL: MM.dd.yyyy).
 *
 * @param {string} lengthUnitValue - Length unit value ("METRIC" or "IMPERIAL")
 * @returns {string} Date format string
 */
export function companyDateFormatted(lengthUnitValue) {
  return lengthUnitValue === LENGTH_UNITS.METRIC ? 'dd.MM.yyyy' : 'MM.dd.yyyy';
}

/**
 * Returns a date-time format string based on the length unit value.
 *
 * @param {string} lengthUnitValue - Length unit value ("METRIC" or "IMPERIAL")
 * @returns {string} Date-time format string
 */
export function companyDateAndTimeFormatted(lengthUnitValue) {
  return lengthUnitValue === LENGTH_UNITS.METRIC ? 'dd.MM.yyyy HH:mm' : 'MM.dd.yyyy hh:mm aa';
}

/**
 * Converts a millisecond timestamp into a formatted date/time string.
 * If the value includes ":", it is returned unchanged.
 *
 * @param {number|string} miliSeconds - Millisecond timestamp or already formatted value
 * @param {string|null} [formatType=null] - Custom format string
 * @param {boolean} [time=true] - Include time in the result
 * @returns {string} Formatted date/time string
 */
export function getDateAndTime(miliSeconds, formatType = null, time = true) {
  if (!miliSeconds) return '';
  if (!miliSeconds.toString().includes(':')) {
    let defaultFormatType;
    const date = new Date(miliSeconds);
    if (time) {
      defaultFormatType = isUsDateFormat() ? 'MM-dd-yyyy aa hh:mm:ss' : 'dd-MM-yyyy HH:mm:ss';
    } else {
      defaultFormatType = isUsDateFormat() ? 'MM-dd-yyyy' : 'dd-MM-yyyy';
    }

    return format(date, formatType || defaultFormatType);
  }
  return miliSeconds;
}

/**
 * Generates an array of time strings in HH:MM format at specified intervals.
 *
 * @param {number} interval - The interval in minutes between each time string. Default is 15 minutes.
 * @returns {string[]} An array of time strings in HH:MM format.
 */
export function timeStrings(interval = 15) {
  const hours = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += interval) {
      hours.push(`${h < 10 ? `0${h}` : h}:${m < 10 ? `0${m}` : m}`);
    }
  }
  return hours;
}

/**
 * Converts an integer date value (YYYYMMDD) into a string or Date object.
 *
 * @param {number|string} intDateValue - Date value in YYYYMMDD format
 * @param {boolean} [stringVersion=false] - Return as string instead of Date
 * @param {string} [seperateMarker='-'] - Separator used in the string output
 * @returns {string|Date} Formatted date string or Date object
 */
export function intToDate(intDateValue, stringVersion = false, seperateMarker = '-') {
  const strVal = String(intDateValue).replaceAll('-', '');
  const year = strVal.slice(0, 4);
  const month = strVal.slice(4, 6);
  const day = strVal.slice(-2);

  return stringVersion ? `${year}${seperateMarker}${month}${seperateMarker}${day}` : new Date(year, month - 1, day);
}

/**
 * Converts a time string (HH:mm) into an integer (HHmm).
 *
 * @param {string} timeStr - Time string in HH:mm format
 * @returns {number|undefined} Integer representation of the time or undefined
 */
export function timeStrToInt(timeStr) {
  if (timeStr === undefined || timeStr === null) return undefined;
  const [hours, minutes] = timeStr.split(':');
  return Number(`${hours}${minutes}`);
}

/**
 * Converts a date string (YYYY-MM-DD) into an integer (YYYYMMDD).
 * If the string has no "-", it is returned as-is.
 *
 * @param {string|number} dateStr - Date string or already numeric value
 * @returns {number|undefined} Integer date value or undefined
 */
export function dateStrToInt(dateStr) {
  if (dateStr === undefined || dateStr === null) return undefined;
  if (![...`${dateStr}`].includes('-')) return dateStr;

  return parseInt(`${dateStr}`.replace(/-/g, ''), 10);
}

export function getDateRangeByType(type, range = {}) {
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  let fromDate;
  let toDate;

  switch (type) {
    case 'today':
      fromDate = format(today, defaults.date);
      toDate = format(today, defaults.date);
      break;
    case 'yesterday':
      fromDate = format(subDays(today, 1), defaults.date);
      toDate = format(subDays(today, 1), defaults.date);
      break;
    case 'lastThirtyDays':
      fromDate = format(subDays(today, 29), defaults.date);
      toDate = format(today, defaults.date);
      break;
    case 'lastSevenDays':
      fromDate = format(subDays(today, 6), defaults.date);
      toDate = format(today, defaults.date);
      break;
    case 'thisMonth':
      fromDate = format(currentMonth, defaults.date);
      toDate = format(lastDayOfMonth(today), defaults.date);
      break;
    case 'lastMonth':
      fromDate = format(startOfMonth(subMonths(today, 1)), defaults.date);
      toDate = format(lastDayOfMonth(subMonths(today, 1)), defaults.date);
      break;
    case 'thisWeek':
      fromDate = format(addDays(startOfWeek(today), 1), defaults.date);
      toDate = format(addDays(endOfWeek(today), 1), defaults.date);
      break;
    case 'nextWeek':
      fromDate = format(addDays(startOfWeek(today), 8), defaults.date);
      toDate = format(addDays(endOfWeek(today), 8), defaults.date);
      break;
    case 'nextMonth':
      fromDate = format(startOfMonth(addMonths(today, 1)), defaults.date);
      toDate = format(lastDayOfMonth(addMonths(today, 1)), defaults.date);
      break;
    case 'customDate':
      fromDate = range.fromDate || format(range.startDate || today, defaults.date);
      toDate = range.toDate || format(range.endDate || today, defaults.date);
      break;
    default:
      fromDate = range.fromDate;
      toDate = range.toDate;
  }

  return {
    type,
    fromDate,
    toDate,
    startDay: dateStrToInt(fromDate),
    endDay: dateStrToInt(toDate),
  };
}

/**
 * Creates an Intl.DateTimeFormat instance with the given timezone and locale.
 *
 * @param {string} timeZone - IANA timezone (e.g. "Europe/Istanbul")
 * @param {string} [locale='en-US'] - Locale used for formatting
 * @param {boolean} [showTime=true] - Whether to include time fields
 * @param {boolean} [hour12=true] - Use 12-hour format if time is shown
 * @returns {Intl.DateTimeFormat} Configured date/time formatter
 */
export function localDateTimeFormatter(timeZone, locale = 'en-US', showTime = true, hour12 = true) {
  const options = {
    day: 'numeric',
    month: 'long',
    timeZone,
  };

  if (showTime) {
    options.hour = 'numeric';
    options.minute = 'numeric';
    options.hour12 = hour12;
  }

  return new Intl.DateTimeFormat(locale, options);
}

/**
 * Formats a date using the local language and a given (or auto-detected) timezone.
 *
 * @param {string|number|Date} dateString - Date value to format
 * @param {string|null} [timezone=null] - Optional IANA timezone (e.g. "Europe/Istanbul")
 * @returns {string} Formatted date string
 */
export function localDateFormatter(dateString, timezone = null) {
  const options = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  return new Intl.DateTimeFormat(navigator.language === 'en-US' ? 'en-US' : 'tr-TR', options).format(dateString);
}

/**
 * Extracts the hour part from a date-time string.
 *
 * @param {string} dateTime - Date-time string in the format "YYYY-MM-DD HH:mm:ss"
 * @returns {string} Hour part of the date-time string
 */
export function extractHourFromDateTimeStr(dateTime) {
  if (!dateTime) return '';
  return dateTime.slice(11, 16);
}

/**
 * Converts a time string (HH:mm:ss) into a comparable number of minutes since midnight.
 *
 * @param {string} date - Time string in HH:mm:ss format
 * @returns {number|null} Comparable number of minutes since midnight or null if invalid
 */
export function hourToComparableNumber(date) {
  if (date === undefined || date === null) {
    return null;
  }
  const isPm = date.toLowerCase().includes('pm');
  const isAm = date.toLowerCase().includes('am');
  const time = date.split(' ')[0];
  let hour = Number(time.split(':')[0]);
  const minute = Number(time.split(':')[1]);

  if (isAm || isPm) {
    if (hour === 12 && isAm) {
      hour = 0;
    } else if (isPm && hour !== 12) {
      hour += 12;
    }
  }

  return hour * 60 + minute;
}

/**
 * Compares two hour strings (e.g. "12:30").
 *
 * @param {string|null} date1 - First hour
 * @param {string|null} date2 - Second hour
 * @returns {number} Comparison result
 */
export function hourComparator(date1, date2) {
  const date1Number = hourToComparableNumber(date1);
  const date2Number = hourToComparableNumber(date2);
  if (date1Number === null && date2Number === null) {
    return 0;
  }
  if (date1Number === null) {
    return -1;
  }
  if (date2Number === null) {
    return 1;
  }
  return date1Number - date2Number;
}

const TIME_TEXT_PATTERN = /^(\d{1,2})[:.](\d{2})\s*([ap]m)?$/i;

/**
 * Converts one displayed time ("16:18", "4:18 PM", "4.18PM") into minutes since midnight.
 * AM/PM is ignored when the hour is already above 12 (e.g. "13.00 AM").
 *
 * @param {string} text - A single time, or a placeholder such as "?"
 * @returns {number|null} Minutes since midnight, or null if the text is not a time
 */
function timeTextToMinutes(text) {
  const match = text.trim().match(TIME_TEXT_PATTERN);
  if (!match) return null;
  const [, hourText, minuteText, meridiem = ''] = match;
  let hour = Number(hourText);
  if (hour <= 12) {
    if (meridiem.toLowerCase() === 'am' && hour === 12) hour = 0;
    if (meridiem.toLowerCase() === 'pm' && hour !== 12) hour += 12;
  }
  return hour * 60 + Number(minuteText);
}

/**
 * Splits a "planned - actual" cell text (as built by plannedActualTime) into its two times.
 *
 * @param {string|null} value - e.g. "12:00 AM - ?", "? - 4:18 PM", "08:30"
 * @returns {Array<number|null>} [planned, actual] in minutes since midnight
 */
export function plannedActualMinutes(value) {
  const [planned = '', actual = ''] = String(value ?? '').split('-');
  return [timeTextToMinutes(planned), timeTextToMinutes(actual)];
}

/**
 * AG Grid comparator for "planned - actual" time cells: sorts by the planned time,
 * then by the actual time. Missing times stay at the bottom in both directions.
 *
 * @param {string|null} valueA
 * @param {string|null} valueB
 * @param {Object} nodeA - unused
 * @param {Object} nodeB - unused
 * @param {boolean} isDescending - passed by AG Grid, which inverts the result for descending sorts
 * @returns {number} Comparison result
 */
export function plannedActualComparator(valueA, valueB, nodeA, nodeB, isDescending = false) {
  const timesA = plannedActualMinutes(valueA);
  const timesB = plannedActualMinutes(valueB);
  const missingLast = isDescending ? -1 : 1;

  for (let i = 0; i < timesA.length; i += 1) {
    const a = timesA[i];
    const b = timesB[i];
    if (a !== b) {
      if (a === null) return missingLast;
      if (b === null) return -missingLast;
      return a - b;
    }
  }
  return 0;
}

/**
 * Formats a time string based on the user's locale preference.
 *
 * @param {string} time - Time string in HH:mm format
 * @returns {string} Formatted time string
 */
export function localFormatTimeString(time) {
  if (!time) return '';
  return isLocaleEnUs() ? convertTo12Hour(time) : time;
}

/**
 * Formats a numeric time value based on the user's locale preference.
 *
 * @param {number|string} time - Time value in HHmm format
 * @returns {string} Formatted time string
 */
export function timeLocalFormat(time) {
  return isLocaleEnUs() ? convertTo12Hour(intToTimeStr(time)) : intToTimeStr(time);
}

/**
 * Formats a Date object based on the user's locale preference.
 *
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateLocal(date) {
  return isLocaleEnUs() ? format(date, 'MMM d, yyyy') : format(date, 'd MMMM yyyy');
}

/**
 * Formats a timestamp into a time string based on the user's locale preference.
 *
 * @param {Date|number} time - Date object or timestamp
 * @returns {string} Formatted time string
 */
export function timeStampLocalFormat(time) {
  return isLocaleEnUs() ? format(time, 'hh:mm a') : format(time, 'HH:mm');
}

/**
 * Formats a timestamp using localized date formats based on user preference.
 *
 * @param {number|Date} time - Timestamp or Date value
 * @returns {string} Localized formatted date string
 */
export function timeStampLocalFormatWithLocalize(time) {
  return isLocaleEnUs()
    ? format(new Date(time), defaults.textFirstMonthDate)
    : format(new Date(time), defaults.textMiddleMonthDate, { locale: tr });
}

// will be deprecated use convertMinutesToHoursAndMinutes in "convert.js"
/**
 * Converts minutes into hours and minutes.
 *
 * @param {number} duration - Total minutes
 * @returns {{hours: number, minutes: number}} Hours and minutes
 */
export function minutesConvert(duration) {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  return { hours, minutes };
}

/**
 * Converts a time string (e.g. "3:45 PM" or "15:45") into 24-hour HH:mm format.
 *
 * @param {string} str - Time string to convert
 * @returns {string} Time in 24-hour format (HH:mm)
 */
export function getTimeFromString(str) {
  // Split the string into hours, minutes, and the am/pm indicator
  const [time, period] = str.split(' ');
  const [hours, minutes] = time.split(':');

  // Convert hours to a number
  let hour = parseInt(hours, 10);

  // Adjust the hour based on the am/pm indicator (if present)
  if (period) {
    if (period.toLowerCase() === 'pm' && hour !== 12) {
      hour += 12;
    } else if (period.toLowerCase() === 'am' && hour === 12) {
      hour = 0;
    }
  }

  // Pad single-digit hours and minutes with leading zeros
  const formattedHour = hour.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  // Return the time as a string in 24-hour format
  return `${formattedHour}:${formattedMinutes}`;
}

/**
 * Extracts time from ISO 8601 string without timezone conversion.
 * This preserves the time in the original timezone without browser conversion.
 *
 * @param {string} isoString - ISO 8601 date-time string (e.g., "2026-01-12T00:13:59-08:00")
 * @returns {string|null} Time in HH:mm format (e.g., "00:13") or null if invalid
 */
export function extractTimeFromISO(isoString) {
  // Input validation
  if (!isoString || typeof isoString !== 'string') {
    return null;
  }

  // Extract time portion from ISO string (e.g., "2026-01-12T00:13:59-08:00" -> "00:13")
  const timeMatch = isoString.match(/T(\d{2}:\d{2})/);
  if (!timeMatch) {
    return null;
  }

  // Validate extracted time is within valid range
  const [hours, minutes] = timeMatch[1].split(':').map(Number);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return timeMatch[1];
}

/**
 * Returns a localized time string (12h for US, 24h otherwise).
 * @param {Date} dateTime
 * @returns {string}
 */
export const formatTimeLocal = (dateTime) =>
  isLocaleEnUs() ? convertTo12Hour(format(dateTime, 'HH:mm')) : format(dateTime, 'HH:mm');

/**
 * Formats planned vs actual values with an optional buffer check.
 * @param {number} planned
 * @param {number} actual
 * @param {number} [buffer=3]
 * @param {string} [seperator='-']
 * @param {boolean} [isMetric=true]
 * @returns {string}
 */
export const plannedActual = (planned, actual, buffer = 3, seperator = '-', isMetric = true) => {
  const computedBuffer = isMetric ? buffer : buffer * (3 / 2);
  let withinBuffer = true;
  if (actual - planned > computedBuffer) withinBuffer = false;
  const plannedFormatted = `<span>${decimalNumber(isMetric ? planned : kmToMiles(planned))}</span>`;
  const actualFormatted = actual
    ? ` ${seperator} <span class="${!withinBuffer ? 'text-danger' : ''}">${decimalNumber(
        isMetric ? actual : kmToMiles(actual),
      )}</span>`
    : null;

  return `${plannedFormatted} ${actualFormatted || ''}`;
};

/**
 * Formats a time window (start–end) and optionally the actual time with late/early styling.
 *
 * @param {string|number|Date} start
 * @param {string|number|Date} end
 * @param {string|number|Date} actual
 * @param {string} direction
 * @returns {string}
 */
export function formattedTimeWindowActual(start, end, actual, direction) {
  const startDateTime = new Date(start);
  const endDateTime = new Date(end);
  const actualDateTime = new Date(actual);

  let isLate = false;
  if (actual) {
    isLate = direction === 'pickup' ? startDateTime < actualDateTime : endDateTime < actualDateTime;
  }

  let str = `${formatTimeLocal(new Date(start))} - ${formatTimeLocal(new Date(end))}`;

  if (actual) {
    str += ` / <span class="${isLate ? 'text-danger' : 'text-success'}">${formatTimeLocal(actualDateTime)}</span>`;
  }
  return str;
}

/**
 * Converts a time or date string/int into a Date object.
 *
 * @param {string|number|Date} strTimeOrDate  Time (HHmm), date string, or Date object.
 * @returns {Date|string|number} A Date object, or the original value if invalid.
 */
export const stringToDate = (strTimeOrDate) => {
  if (!strTimeOrDate) return strTimeOrDate;
  if (Object.prototype.toString.call(strTimeOrDate) !== '[object Date]') {
    if (strTimeOrDate.toString().length < 5) {
      return new Date(`${new Date().toISOString().split('T')[0]} ${intToTimeStr(strTimeOrDate)}`);
    }
    return new Date(strTimeOrDate);
  }
  return strTimeOrDate;
};

/**
 * Formats planned and actual time values and highlights delays.
 *
 * @param {string|number|Date} plannedStr
 * @param {string|number|Date} actualStr
 * @param {number} [bufferInMin=0]
 * @param {string} [seperator='-']
 * @returns {string}
 */
export const plannedActualTime = (plannedStr, actualStr, bufferInMin = 0, seperator = '-') => {
  if (!plannedStr && !actualStr) return '';
  const planned = stringToDate(plannedStr);
  const actual = stringToDate(actualStr);

  const diff = differenceInMinutes(actual, planned) || undefined;

  const formattedPlanned = planned ? `<span>${formatTimeLocal(planned)}</span>` : ' ? ';
  const formattedActual = actual
    ? ` ${seperator} <span class="${diff > 0 + bufferInMin ? 'text-danger' : 'text-success'}">${formatTimeLocal(
        actual,
      )}</span>`
    : '';

  return `${formattedPlanned}${formattedActual}`;
};

/**
 * Calculates planned vs actual duration (in minutes) and returns formatted HTML.
 *
 * @param {string|number|Date} offerPickupTimeStart   Planned pickup time.
 * @param {string|number|Date} offerDeliveryTimeStart Planned delivery time.
 * @param {string|number|Date} actualDeliveryDate     Actual delivery time.
 * @param {string|number|Date} actualPickupDate       Actual pickup time.
 * @returns {string} HTML-formatted planned vs actual duration.
 */
export const plannedActualDurationMin = (
  offerPickupTimeStart,
  offerDeliveryTimeStart,
  actualDeliveryDate,
  actualPickupDate,
) => {
  const planned =
    offerPickupTimeStart && offerDeliveryTimeStart
      ? differenceInMinutes(
          stringToDate(offerDeliveryTimeStart).setSeconds(0),
          stringToDate(offerPickupTimeStart).setSeconds(0),
        )
      : '';
  const actual =
    actualDeliveryDate && actualPickupDate
      ? differenceInMinutes(
          stringToDate(actualDeliveryDate).setSeconds(0),
          stringToDate(actualPickupDate).setSeconds(0),
        )
      : '';
  const formatText =
    actual !== ''
      ? `<span>${planned} - </span> <span class="${actual > planned ? 'text-danger' : 'text-success'}">${actual}</span>`
      : `<span>${planned}</span>`;

  return `${formatText}`;
};

/**
 * Detects whether the user's locale uses 24-hour time format.
 *
 * @returns {boolean} True if 24h format is used, otherwise false.
 */
export function is24HourFormat() {
  // Don't specify hourCycle - let the locale determine it
  const formattedTime = new Intl.DateTimeFormat(navigator.language, {
    hour: 'numeric',
  }).format(new Date());

  return !formattedTime.includes('AM') && !formattedTime.includes('PM');
}

/**
 * Subtracts minutes from a datetime string and returns the result in the same format.
 *
 * @param {string} dateStr        Datetime string in "yyyy-MM-dd HH:mm:ss" format.
 * @param {number} minutesToSub   Minutes to subtract.
 * @returns {string} New datetime string in "yyyy-MM-dd HH:mm:ss" format.
 */
export function addInMinutes(dateStr, minutesToAdd) {
  const date = parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date());
  const newDate = addMinutes(date, minutesToAdd);
  const formattedDate = format(newDate, 'yyyy-MM-dd HH:mm:ss');
  return formattedDate;
}

/**
 * Subtracts minutes from a datetime string.
 *
 * @param {string} dateStr - Datetime in "yyyy-MM-dd HH:mm:ss" format.
 * @param {number} minutesToSub - Minutes to subtract.
 * @returns {string} Updated datetime string in the same format.
 */
export function subInMinutes(dateStr, minutesToSub) {
  const date = parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date());
  const newDate = subMinutes(date, minutesToSub);
  const formattedDate = format(newDate, 'yyyy-MM-dd HH:mm:ss');
  return formattedDate;
}

/**
 * Checks whether a given time is within a start–end time window.
 * Handles windows that pass midnight (e.g. 22:00–03:00).
 *
 * @param {string} time  - Time in "HH:mm" format.
 * @param {string} start - Window start time in "HH:mm" format.
 * @param {string} end   - Window end time in "HH:mm" format.
 * @returns {boolean} True if the time is inside the window, otherwise false.
 */
export function isInTimeInWindow(time, start, end) {
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);
  const [hour, minute] = time.split(':').map(Number);

  const startTime = startHour * 60 + startMinute; // Convert to minutes since midnight
  const endTime = endHour * 60 + endMinute;
  const givenTime = hour * 60 + minute;

  if (endTime < startTime) {
    // Time window spans midnight
    return givenTime >= startTime || givenTime < endTime;
  }
  return givenTime >= startTime && givenTime < endTime;
}

/**
 * Formats a date into a long, readable format (March 10, 2025).
 *
 * @param {Date|string|number} date - A date value convertible to a Date object.
 * @returns {string} Formatted long date string.
 */
export function formattedDateLong(date) {
  if (!date) return '';

  return date.toLocaleString(navigator.language || navigator.userLanguage, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a date into a full date-time string based on the user's locale.
 *
 * @param {Date} date - A JavaScript Date object.
 * @returns {string} Formatted date-time string.
 */
export function formattedDateFull(date) {
  if (!date) return '';
  return date.toLocaleString(navigator.language || navigator.userLanguage, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
}

/**
 * Converts a date string to a localized date string using the browser's locale.
 *
 * @param {string|number} dateString - A date value convertible to a Date object.
 * @param {Object} [options={}] - Optional Intl.DateTimeFormat options.
 * @returns {string} Localized date string, or empty string if invalid.
 */
export function toLocaleDateString(dateString, options = {}) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    console.error('Invalid date:', dateString);
    return '';
  }
  return date.toLocaleDateString(navigator.language || navigator.userLanguage, options);
}

/**
 * Calculates the total time in minutes between two dates.
 * If the arrival date is earlier than the departure date, one day is added to the arrival date.
 *
 * @param {Date} departureDate - The departure datetime.
 * @param {Date} arrivalDate - The arrival datetime.
 * @returns {number} Total minutes between the two dates.
 */
export function calculateTotalTime(departureDate, arrivalDate) {
  const adjustedArrivalDate = isBefore(arrivalDate, departureDate) ? addDays(arrivalDate, 1) : arrivalDate;
  const totalMinutes = differenceInMinutes(adjustedArrivalDate, departureDate);
  return totalMinutes;
}

/**
 * Converts a Date object into an integer in YYYYMMDD format.
 * @param {Date} date - The date to convert.
 * @returns {number|undefined} The date as YYYYMMDD, or undefined if invalid.
 */
export function dateToInt(date) {
  if (!date || !(date instanceof Date)) return undefined;
  return parseInt(format(date, 'yyyyMMdd'), 10);
}

/**
 * Builds a local Date from two day/month parts and a year, disambiguating day
 * vs. month when one part can't be a month (e.g. "13.02.2026" → day=13 even in
 * a US locale), and validating the calendar date so rollovers like 31.02 →
 * 03.03 are rejected.
 *
 * Convention: `a` is the first part of the source string, `b` the second. In a
 * US locale `a` is treated as the month; otherwise `a` is the day.
 *
 * @param {number} a - First part (day in non-US locale, month in US locale).
 * @param {number} b - Second part (month in non-US locale, day in US locale).
 * @param {number} year - 4-digit year.
 * @returns {Date|null} Valid local Date, or null if the parts don't form a real date.
 */
export function buildLocalDate(a, b, year) {
  let day;
  let month;
  if (a > 12 && b <= 12) {
    day = a;
    month = b;
  } else if (b > 12 && a <= 12) {
    day = b;
    month = a;
  } else {
    day = isUsDateFormat() ? b : a;
    month = isUsDateFormat() ? a : b;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000 || year > 9999) return null;
  const date = new Date(year, month - 1, day);
  // Reject calendar rollovers (e.g. new Date(2026, 1, 31) → March 3).
  if (date.getDate() !== day || date.getMonth() !== month - 1) return null;
  return date;
}

/**
 * Parses various date formats into a valid Date object.
 * Handles YYYYMMDD integers, ISO strings, and Date objects.
 *
 * @param {string|number|Date} dateValue - Date in various formats
 * @returns {Date|null} Valid Date object or null if invalid
 */
export function parseFlexibleDate(dateValue) {
  if (!dateValue) return null;

  // Already a Date object
  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  // Handle YYYYMMDD integer format (e.g., 20240127)
  if (typeof dateValue === 'number' || /^\d{8}$/.test(String(dateValue))) {
    return intToDate(dateValue);
  }

  // Handle DD.MM.YYYY or MM.DD.YYYY format with dots
  // new Date() misparses these (e.g. "02.07.2026" → Feb 7 instead of Jul 2)
  const dotMatch = String(dateValue).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dotMatch) {
    const [, a, b, year] = dotMatch.map(Number);
    return buildLocalDate(a, b, year);
  }

  // Handle ISO strings and other date string formats
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Converts a Date object to DD.MM.YYYY formatted string.
 * Used for notification filtering and display.
 *
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string in DD.MM.YYYY format or empty string if invalid
 */
export function dateToFormattedString(date) {
  if (!date || !(date instanceof Date)) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Detects the time format from a value.
 * @param {string|number} timeValue - Time value to detect format
 * @returns {'HH:mm'|'HHmm'|'ISO'|'unknown'} Detected format
 */
function detectTimeFormat(timeValue) {
  if (typeof timeValue === 'number' || /^\d{3,4}$/.test(String(timeValue))) {
    // Validate HHmm format: hours 0-23, minutes 0-59
    const timeStr = String(timeValue).padStart(4, '0');
    const hours = parseInt(timeStr.slice(0, 2), 10);
    const minutes = parseInt(timeStr.slice(2, 4), 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return 'HHmm'; // Integer format like 1230 or 630
    }
    return 'unknown'; // Invalid time value
  }
  if (typeof timeValue === 'string') {
    if (/^\d{2}:\d{2}$/.test(timeValue)) {
      // Validate HH:mm format: hours 0-23, minutes 0-59
      const [h, m] = timeValue.split(':').map(Number);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return 'HH:mm'; // String format like "12:30"
      }
      return 'unknown'; // Invalid time value
    }
    if (/T\d{2}:\d{2}/.test(timeValue)) {
      return 'ISO'; // ISO format like "2026-01-12T00:13:59-08:00"
    }
  }
  return 'unknown';
}

/**
 * Normalizes a time value to minutes since midnight.
 * @param {string|number} timeValue - Time in various formats
 * @returns {number|null} Minutes since midnight or null if invalid
 */
export function normalizeToMinutes(timeValue) {
  if (timeValue === null || timeValue === undefined) return null;

  const timeFormat = detectTimeFormat(timeValue);

  let hours = 0;
  let minutes = 0;

  switch (timeFormat) {
    case 'HHmm': {
      const timeStr = String(timeValue).padStart(4, '0');
      hours = parseInt(timeStr.slice(0, 2), 10);
      minutes = parseInt(timeStr.slice(2, 4), 10);
      break;
    }
    case 'HH:mm': {
      const [h, m] = timeValue.split(':').map(Number);
      hours = h;
      minutes = m;
      break;
    }
    case 'ISO': {
      const extracted = extractTimeFromISO(timeValue);
      if (!extracted) return null;
      const [h, m] = extracted.split(':').map(Number);
      hours = h;
      minutes = m;
      break;
    }
    default:
      return null;
  }

  return hours * 60 + minutes;
}

/**
 * Converts minutes since midnight to specified time format.
 * @param {number} totalMinutes - Minutes since midnight
 * @param {'HH:mm'|'HHmm'} targetFormat - Target format
 * @returns {string|number} Formatted time value
 */
export function minutesToTimeFormat(totalMinutes, targetFormat) {
  // Normalize to 0-1439 range (24 hours)
  let normalizedMinutes = totalMinutes % 1440;
  if (normalizedMinutes < 0) {
    normalizedMinutes += 1440;
  }

  const hours = Math.floor(normalizedMinutes / 60);
  const mins = Math.floor(normalizedMinutes % 60);

  if (targetFormat === 'HHmm') {
    return hours * 100 + mins;
  }

  // Default to 'HH:mm'
  const hoursStr = String(hours).padStart(2, '0');
  const minsStr = String(mins).padStart(2, '0');
  return `${hoursStr}:${minsStr}`;
}

/**
 * Adds or subtracts time from a given time value with flexible format support.
 * Automatically detects input format and returns result in specified or detected format.
 *
 * @param {string|number} timeValue - Time in various formats (HH:mm, HHmm, ISO)
 * @param {number} minutesToAddOrSubtract - Minutes to add (positive) or subtract (negative)
 * @param {Object} [options={}] - Optional configuration
 * @param {'HH:mm'|'HHmm'|null} [options.outputFormat=null] - Target format (null = auto-detect from input)
 * @returns {string|number|null} Calculated time in specified format, or null if invalid
 *
 * @example
 * // Add 30 minutes to "12:30" -> "13:00"
 * adjustTime("12:30", 30)
 *
 * @example
 * // Subtract 45 minutes from 1230 -> 1145
 * adjustTime(1230, -45)
 *
 * @example
 * // Add 90 minutes to ISO time, return as HH:mm
 * adjustTime("2026-01-12T00:13:59-08:00", 90, { outputFormat: "HH:mm" })
 *
 * @example
 * // Subtract service time (in seconds) from departure time
 * adjustTime("08:30", -Math.round(120 / 60)) // 120 seconds = 2 minutes -> "08:28"
 */
export function adjustTime(timeValue, minutesToAddOrSubtract, options = {}) {
  const { outputFormat = null } = options;

  // Convert input to minutes since midnight
  const currentMinutes = normalizeToMinutes(timeValue);
  if (currentMinutes === null) return null;

  // Add or subtract minutes
  const newMinutes = currentMinutes + minutesToAddOrSubtract;

  // Determine output format
  let targetFormat = outputFormat;
  if (!targetFormat) {
    const detectedFormat = detectTimeFormat(timeValue);
    targetFormat = detectedFormat === 'ISO' ? 'HH:mm' : detectedFormat;
  }

  // Convert back to target format
  return minutesToTimeFormat(newMinutes, targetFormat);
}

/**
 * Converts a timestamp or Date object to YYYY-MM-DD string format.
 * This function is timezone-insensitive - it extracts date components directly
 * from the Date object to avoid timezone conversion issues.
 *
 * @param {number|Date} timestamp - Timestamp in milliseconds or Date object
 * @returns {string} Date string in YYYY-MM-DD format, or empty string if invalid
 *
 * @example
 * // Convert timestamp to date string
 * dateToString(1709942400000) // "2024-03-09"
 *
 * @example
 * // Convert Date object to date string
 * dateToString(new Date(2024, 2, 9)) // "2024-03-09"
 */
export function dateToString(timestamp) {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a timestamp to time string with timezone support and locale-aware formatting.
 * Automatically uses 12-hour format for US locale, 24-hour for others.
 *
 * @param {number|Date} timestamp - Timestamp in milliseconds or Date object
 * @param {string} [timezone='UTC'] - IANA timezone (e.g., "America/Los_Angeles", "Europe/Istanbul")
 * @returns {string} Formatted time string (e.g., "02:30 PM" for US, "14:30" for EU)
 *
 * @example
 * // Format timestamp in Los Angeles timezone for US locale
 * formatTimeWithTimezone(1773327600000, "America/Los_Angeles") // "02:30 PM"
 *
 * @example
 * // Format timestamp in Istanbul timezone for EU locale
 * formatTimeWithTimezone(1773327600000, "Europe/Istanbul") // "14:30"
 */
export function formatTimeWithTimezone(timestamp, timezone = 'UTC') {
  if (!timestamp) return '';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const locale = isLocaleEnUs() ? 'en-US' : 'tr-TR';
  const hour12 = isLocaleEnUs();

  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12,
    timeZone: timezone,
  };

  return new Intl.DateTimeFormat(locale, options).format(date);
}
